import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Play, HardHat, Cpu, HeartPulse, Landmark, ShoppingBag, Building2,
  Briefcase, GraduationCap, Factory, Zap, Train, FlaskConical, Shield, Radio,
  CalendarRange, LayoutGrid, ListChecks, Flag, TriangleAlert, PoundSterling, FileText, BarChart3,
} from "lucide-react";
import api from "@/lib/api";
import { MarketingFooter } from "@/components/marketing/MarketingChrome";
import { MarketingNav } from "@/components/marketing/MarketingChrome";

const NAVY = "#071426";
const CHARCOAL = "#101827";
const BLUE = "#2585FF";
const TEAL = "#19C5C5";
const GREY = "#F3F6FA";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1636714289409-e06a4dfc617c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwY3JhbmVzJTIwZHVza3xlbnwwfHx8fDE3OTE3NTIzNzh8MA&ixlib=rb-4.1.0&q=85";

// Every one of these is a real sector template in the product. Nothing here is aspirational.
const SECTORS = [
  ["construction", "Construction", HardHat, "Scheme delivery with work stages, contractors and site progress."],
  ["it", "IT & Technology", Cpu, "Software and infrastructure delivery across agile and waterfall gates."],
  ["healthcare", "Healthcare", HeartPulse, "Clinically governed change with patient safety and information governance."],
  ["public_sector", "Public Sector", Landmark, "Business-case driven delivery aligned to Green Book style gates."],
  ["financial_services", "Financial Services", Building2, "Regulated change with conduct, resilience and control testing."],
  ["professional_services", "Professional Services", Briefcase, "Client engagement delivery with utilisation, scope and margin risk."],
  ["manufacturing", "Manufacturing", Factory, "Line and product introduction with tooling, quality and downtime."],
  ["retail_hospitality", "Retail & Hospitality", ShoppingBag, "Site rollouts and change that respects the trading calendar."],
  ["energy_utilities", "Energy & Utilities", Zap, "Regulated asset investment with outage and consent planning."],
  ["transport", "Transport", Train, "Network delivery with possessions and passenger impact."],
  ["education", "Education", GraduationCap, "Academic-calendar change with safeguarding and curriculum gates."],
  ["life_sciences", "Life Sciences", FlaskConical, "GxP-validated change with submission and validation evidence."],
  ["defence", "Defence", Shield, "Capability acquisition with clearance and export control."],
  ["telecoms", "Telecoms", Radio, "Network build and migration with permits and service continuity."],
];

const CAPABILITIES = [
  [CalendarRange, "Project Planning and Tracking"], [LayoutGrid, "Portfolio Management and Reporting"],
  [ListChecks, "Task Management"], [Flag, "Programme and Milestone Tracking"],
  [TriangleAlert, "Risk and Issue Management"], [PoundSterling, "Financial and Commercial Controls"],
  [FileText, "Document Management"], [BarChart3, "Dashboards and Reporting"],
];

const STEPS = [
  ["Choose Your Template", "Select the industry template that best matches your business."],
  ["Make It Yours", "Personalise the settings and workflows to suit your requirements."],
  ["Start Delivering", "Bring your projects, teams and reporting together in one workspace."],
];

const SELECTOR = {
  construction: ["Construction Project Management",
    "Deliver construction projects on time and on budget with a template designed for the built environment.",
    ["Work stage and phase tracking", "Contract and commercial management", "Risk and issue tracking",
      "Document management", "Site progress reporting"], "/tour/project.jpg"],
  it: ["IT and Technology Delivery",
    "Run software and infrastructure change across agile and waterfall gates in one place.",
    ["Release and milestone tracking", "Dependency management", "Change control",
      "Cutover and go-live planning", "Benefits tracking"], "/tour/plan.jpg"],
  healthcare: ["Healthcare Programme Delivery",
    "Clinically governed change with patient safety and information governance built in.",
    ["Clinical safety gates", "Information governance evidence", "Business case approvals",
      "Benefits realisation", "Board-ready reporting"], "/tour/compliance.jpg"],
  public_sector: ["Public Sector Delivery",
    "Business-case driven delivery aligned to Green Book style gates and public accountability.",
    ["Gate reviews and approvals", "Assurance scoring", "Spend and commitment control",
      "Decision and audit trail", "Transparency reporting"], "/tour/reports.jpg"],
};

const Section = ({ id, dark, tint, children, className = "" }) => (
  <section
    id={id}
    className={`px-6 py-20 lg:px-10 lg:py-28 ${className}`}
    style={{ backgroundColor: dark ? NAVY : tint ? GREY : "#FFFFFF" }}
  >
    <div className="mx-auto max-w-7xl">{children}</div>
  </section>
);

const Eyebrow = ({ children, dark }) => (
  <p className="text-[11px] font-bold uppercase tracking-[0.18em]"
    style={{ color: dark ? TEAL : BLUE }}>{children}</p>
);

const H2 = ({ children, dark }) => (
  <h2 className="mt-3 font-marketing-display text-3xl font-black leading-[1.08] tracking-tight md:text-4xl lg:text-[2.7rem]"
    style={{ color: dark ? "#FFFFFF" : NAVY }}>{children}</h2>
);

const Primary = ({ to, children, testId, href }) => {
  const cls = "inline-flex items-center justify-center gap-2 rounded-md px-6 py-3.5 text-sm font-bold text-white shadow-lg transition-transform duration-200 hover:-translate-y-0.5";
  const style = { backgroundColor: BLUE, boxShadow: "0 10px 30px -12px rgba(37,133,255,0.8)" };
  return href
    ? <a href={href} data-testid={testId} className={cls} style={style}>{children} <ArrowRight size={15} /></a>
    : <Link to={to} data-testid={testId} className={cls} style={style}>{children} <ArrowRight size={15} /></Link>;
};

const Ghost = ({ to, children, testId, dark }) => (
  <Link to={to} data-testid={testId}
    className={`inline-flex items-center justify-center gap-2 rounded-md border-2 px-6 py-3.5 text-sm font-bold transition-colors ${
      dark ? "border-white/25 text-white hover:border-white/60" : "border-slate-300 text-slate-900 hover:border-slate-900"}`}>
    {children} <ArrowRight size={15} />
  </Link>
);

export default function Home() {
  const [plans, setPlans] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [tab, setTab] = useState("construction");

  useEffect(() => {
    api.get("/public/plans").then(({ data }) => setPlans(data)).catch(() => setPlans(null));
  }, []);

  const [selTitle, selBody, selPoints, selShot] = SELECTOR[tab];

  return (
    <div className="marketing-root" data-testid="home-page">
      <MarketingNav active="Platform" />

      {/* 1 — Hero */}
      <header className="relative overflow-hidden" style={{ backgroundColor: NAVY }} id="platform">
        <img src={HERO_IMAGE} alt="" aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div aria-hidden="true" className="absolute inset-0"
          style={{ background: `linear-gradient(100deg, ${NAVY} 8%, rgba(7,20,38,0.92) 46%, rgba(7,20,38,0.55) 100%)` }} />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 py-20 lg:grid-cols-[1.05fr_1fr] lg:px-10 lg:py-28">
          <div>
            <h1 className="font-marketing-display text-4xl font-black leading-[1.04] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Every Project.<br />
              <span style={{ color: TEAL }}>One Powerful</span> Platform.
            </h1>
            <p className="mt-6 text-lg font-semibold text-white/90">
              Plan, manage and deliver projects with confidence.
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-white/70">
              Videra PM brings your projects, people, programmes, budgets and reporting together in
              one intelligent workspace. Start with an industry-ready template, customise your
              platform and take control of project delivery.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Primary to="/signup" testId="hero-trial">Start Your Free Trial</Primary>
              <Ghost to="/tour" testId="hero-explore" dark>Explore the Platform</Ghost>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
              {["Industry-ready templates", "Flexible and scalable", "No lengthy implementation"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2 text-xs font-semibold text-white/75">
                  <Check size={14} style={{ color: TEAL }} /> {t}
                </span>
              ))}
            </div>
          </div>
          <div className="relative" data-testid="hero-shot">
            <div className="rounded-xl border border-white/15 bg-white/5 p-2 shadow-2xl backdrop-blur-sm">
              <img src="/tour/dashboard.jpg" alt="The Videra PM portfolio dashboard"
                className="block w-full rounded-lg" />
            </div>
            <div aria-hidden="true" className="mx-auto h-3 w-3/4 rounded-b-xl bg-white/10" />
          </div>
        </div>
      </header>

      {/* 2 — Industries */}
      <Section id="industries" tint>
        <Eyebrow>Industries</Eyebrow>
        <H2>Built Around the Way You Work.</H2>
        <p className="mt-4 max-w-3xl text-base text-slate-600">
          Every industry manages projects differently. Videra PM gives you the flexibility to start
          with a relevant template and adapt your workspace to your business. All fourteen below are
          real templates you can load on day one.
        </p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SECTORS.map(([key, name, Icon, body]) => (
            <Link key={key} to={`/signup?sector=${key}`} data-testid={`industry-${key}`}
              className="group rounded-lg border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-transparent hover:shadow-xl">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-md"
                style={{ backgroundColor: "rgba(37,133,255,0.1)" }}>
                <Icon size={20} style={{ color: BLUE }} strokeWidth={1.75} />
              </span>
              <h3 className="mt-4 text-base font-bold" style={{ color: NAVY }}>{name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold"
                style={{ color: BLUE }}>
                View Template <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </Section>

      {/* 3 — Capabilities */}
      <Section id="features">
        <div className="grid items-center gap-14 lg:grid-cols-[1fr_1.15fr]">
          <div>
            <Eyebrow>Capabilities</Eyebrow>
            <H2>Everything You Need to Deliver.</H2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Videra PM gives you the tools to plan, track and deliver projects, programmes and
              portfolios in one platform.
            </p>
            <div className="mt-8"><Primary to="/tour" testId="features-explore">Explore All Features</Primary></div>
            <div className="mt-10 grid gap-x-8 gap-y-4 sm:grid-cols-2">
              {CAPABILITIES.map(([Icon, label]) => (
                <span key={label} className="flex items-start gap-3 text-sm font-semibold text-slate-700">
                  <Icon size={17} style={{ color: BLUE }} strokeWidth={1.75} className="mt-0.5 shrink-0" />
                  {label}
                </span>
              ))}
            </div>
          </div>
          <div className="relative" data-testid="capabilities-shots">
            <img src="/tour/plan.jpg" alt="Project planning in Videra PM"
              className="w-full rounded-lg border border-slate-200 shadow-2xl" />
            <img src="/tour/reports.jpg" alt="Financial and portfolio reporting"
              className="absolute -bottom-10 -right-4 hidden w-1/2 rounded-lg border border-slate-200 shadow-2xl lg:block" />
          </div>
        </div>
      </Section>

      {/* 4 — Three steps */}
      <Section dark>
        <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <H2 dark>Powerful Project Management.<br />Without the Complexity.</H2>
            <p className="mt-5 text-base leading-relaxed text-white/70">
              Start with an industry-ready template, personalise your workspace and begin managing
              projects.
            </p>
            <p className="mt-4 text-base leading-relaxed text-white/70">
              No lengthy implementation programme. No expensive consultancy engagement. Just a
              flexible platform designed to grow with your business.
            </p>
          </div>
          <div className="grid gap-8 sm:grid-cols-3">
            {STEPS.map(([title, body], i) => (
              <div key={title} data-testid={`step-${i + 1}`}>
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-black"
                  style={{ border: `2px solid ${TEAL}`, color: TEAL }}>{i + 1}</span>
                <h3 className="mt-5 text-base font-bold text-white">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/65">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* 5 — Industry selector */}
      <Section tint>
        <Eyebrow>Industry selector</Eyebrow>
        <H2>Discover the Right Platform for Your Industry.</H2>
        <p className="mt-4 max-w-2xl text-base text-slate-600">
          Select your industry to see how Videra PM can support your specific project management
          needs.
        </p>
        <div className="mt-8 flex flex-wrap gap-2">
          {Object.keys(SELECTOR).map((key) => {
            const label = SECTORS.find((s) => s[0] === key)[1];
            const on = tab === key;
            return (
              <button key={key} data-testid={`selector-tab-${key}`} onClick={() => setTab(key)}
                className="rounded-full px-4 py-2 text-sm font-bold transition-colors"
                style={on
                  ? { backgroundColor: BLUE, color: "#fff" }
                  : { backgroundColor: "#fff", color: NAVY, border: "1px solid #e2e8f0" }}>
                {label}
              </button>
            );
          })}
        </div>
        <div className="mt-8 grid items-center gap-10 lg:grid-cols-2" data-testid="selector-panel">
          <img src={selShot} alt={selTitle} className="w-full rounded-lg border border-slate-200 shadow-xl" />
          <div>
            <h3 className="text-xl font-bold" style={{ color: NAVY }}>{selTitle}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{selBody}</p>
            <ul className="mt-5 space-y-2.5">
              {selPoints.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm font-medium text-slate-700">
                  <Check size={16} style={{ color: TEAL }} className="mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <div className="mt-7">
              <Primary to={`/signup?sector=${tab}`} testId="selector-cta">
                Start with this template
              </Primary>
            </div>
          </div>
        </div>
      </Section>

      {/* 6 — Product demo */}
      <Section dark>
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_1fr]">
          <div>
            <Eyebrow dark>Product demo</Eyebrow>
            <H2 dark>See the Bigger Picture.<br />Control Every Detail.</H2>
            <p className="mt-5 text-base leading-relaxed text-white/70">
              Explore the Videra PM platform and see how it brings your projects, people, programmes
              and reporting together in one workspace.
            </p>
            {/* Placeholder until the narrated walkthrough is rendered: it opens the real guided
                tour rather than faking a player. Drop a URL in and this becomes a video. */}
            <div className="mt-8"><Primary to="/tour" testId="demo-cta">
              <Play size={15} /> Take the Guided Tour
            </Primary></div>
            <p className="mt-3 text-xs text-white/45">
              The narrated video walkthrough is in production and lands here shortly.
            </p>
          </div>
          <div className="relative">
            <img src="/tour/raid.jpg" alt="Live project registers in Videra PM"
              className="w-full rounded-lg border border-white/15 shadow-2xl" />
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {["Interactive dashboards", "Real project data", "Easy to customise", "Designed for real teams"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2 text-xs font-semibold text-white/75">
                  <Check size={13} style={{ color: TEAL }} /> {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* 7 — Pricing (live values from /api/public/plans) */}
      <Section id="pricing" tint>
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <Eyebrow>Pricing</Eyebrow>
            <H2>Professional Project Management. Flexible Pricing.</H2>
            <p className="mt-4 text-base text-slate-600">
              Start with a {plans?.trial_days || 7} day free trial and choose the plan that is right
              for your business. Priced per user, so it scales with your team rather than ahead of it.
            </p>
            <div className="mt-6 inline-flex rounded-full bg-white p-1 shadow-sm">
              {[["Monthly", false], ["Annually", true]].map(([label, val]) => (
                <button key={label} data-testid={`home-billing-${val ? "annual" : "monthly"}`}
                  onClick={() => setAnnual(val)}
                  className="rounded-full px-4 py-1.5 text-sm font-bold transition-colors"
                  style={annual === val ? { backgroundColor: BLUE, color: "#fff" } : { color: NAVY }}>
                  {label}{val && <span className="ml-1.5 text-xs font-semibold" style={{ color: annual ? "#fff" : TEAL }}>Save 20%</span>}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            {(plans?.plans || []).map((p) => {
              const hero = !!p.recommended;
              return (
                <div key={p.key} data-testid={`home-plan-${p.key}`}
                  className="relative flex flex-col rounded-xl border bg-white p-6 shadow-sm"
                  style={hero ? { borderColor: BLUE, borderWidth: 2, boxShadow: "0 24px 50px -28px rgba(37,133,255,0.55)" } : { borderColor: "#e2e8f0" }}>
                  {hero && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-wider text-white"
                      style={{ backgroundColor: BLUE }}>Most Popular</span>
                  )}
                  <h3 className="text-sm font-bold" style={{ color: NAVY }}>{p.name}</h3>
                  <p className="mt-3">
                    <span className="font-marketing-display text-3xl font-black" style={{ color: NAVY }}>
                      {p.quote_only ? "Let's talk" : `£${annual ? p.price_annual : p.price_monthly}`}
                    </span>
                    {!p.quote_only && <span className="text-xs font-semibold text-slate-500"> /user/month</span>}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    {p.quote_only
                      ? `From ${p.min_seats} users, priced on your requirements.`
                      : `${annual ? "Billed annually" : "Billed monthly"}, from ${p.min_seats} user${p.min_seats === 1 ? "" : "s"}.`}
                  </p>
                  <ul className="mt-5 flex-1 space-y-2">
                    {(p.headline_points || []).slice(0, 5).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs leading-relaxed text-slate-600">
                        <Check size={13} style={{ color: TEAL }} className="mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link to={p.quote_only ? "/contact" : `/signup?plan=${p.key}`}
                    data-testid={`home-plan-cta-${p.key}`}
                    className="mt-6 inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: hero ? BLUE : NAVY }}>
                    {p.quote_only ? "Talk to us" : "Start Free Trial"}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* 8 — Enterprise */}
      <Section id="enterprise" dark>
        <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <Eyebrow dark>Enterprise</Eyebrow>
            <H2 dark>Need Something More Specialist?</H2>
            <p className="mt-5 text-base leading-relaxed text-white/70">
              Videra PM provides a flexible starting point for organisations looking to improve
              project delivery. For businesses requiring more specialised functionality, complex
              workflows or enterprise-level solutions, Videra Technology develops bespoke platforms
              through Keystone Consulting.
            </p>
            <div className="mt-8">
              <Primary to="/enterprise" testId="enterprise-cta">
                Explore Enterprise Solutions
              </Primary>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {[["Specialist functionality", "Industry-specific platforms"],
              ["Complex workflows", "Tailored to your organisation"],
              ["Enterprise solutions", "Built for your business"]].map(([t, b]) => (
              <div key={t} className="rounded-lg border border-white/15 bg-white/5 p-5">
                <p className="text-sm font-bold text-white">{t}</p>
                <p className="mt-1.5 text-xs leading-relaxed text-white/60">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* 9 — Closing CTA */}
      <Section>
        <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <div>
            <H2>Take Control of Your Projects Today.</H2>
            <p className="mt-4 text-base text-slate-600">
              Discover a smarter way to plan, manage and deliver projects with Videra PM.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Primary to="/signup" testId="closing-trial">Start Your Free Trial</Primary>
            <Ghost to="/pricing" testId="closing-plans">View Subscription Plans</Ghost>
          </div>
        </div>
      </Section>

      <MarketingFooter />
    </div>
  );
}
